import React, { useMemo, useState, useEffect } from "react";
import {
  PROFILE_QUESTIONS, CORE_QUESTIONS, INDUSTRY_MODULES, NOT_SURE_LABEL,
  resolveModuleKey, FIRM,
} from "../data/assessment";
import { computeReport } from "../lib/scoring";
import { recordNavigatorResult } from "../lib/api";
import Report from "../components/tcc/Report";
import { ArrowLeft, ArrowRight, ShieldCheck, Lock, Clock, CheckCircle2 } from "lucide-react";

export default function CyberRiskNavigatorPage() {
  const [phase, setPhase] = useState("intro"); // intro | quiz | report
  const [screenIdx, setScreenIdx] = useState(0);
  const [lead, setLead] = useState({ name: "", company: "", email: "", mobile: "" });
  const [answers, setAnswers] = useState({}); // Q id -> array of selected labels (multi-select for core+industry, single for profile)
  const [profile, setProfile] = useState({});
  const [touched, setTouched] = useState(false);
  const [showInlineError, setShowInlineError] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [phase, screenIdx]);

  // Build the ordered list of screens dynamically.
  const screens = useMemo(() => {
    const profileQs = PROFILE_QUESTIONS.filter((q) => !q.conditional || q.conditional(profile));
    const coreScreens = [1, 2, 3, 4].map((n) => ({
      kind: "core",
      title: coreTitle(n),
      questions: CORE_QUESTIONS.filter((q) => q.screen === n),
    }));
    const arr = [
      { kind: "profile", title: "About your business", questions: profileQs },
      ...coreScreens,
    ];
    const key = resolveModuleKey(profile);
    if (key !== "other" && INDUSTRY_MODULES[key]) {
      arr.push({ kind: "industry", title: "Your industry & compliance", questions: INDUSTRY_MODULES[key] });
    }
    return arr;
  }, [profile]);

  const totalScreens = screens.length;
  const current = screens[screenIdx];
  const progress = Math.round(((screenIdx) / totalScreens) * 100);

  const setAnswer = (q, value) => {
    if (q.id.startsWith("A")) {
      // Profile questions remain single-select
      setProfile((p) => {
        const next = { ...p, [q.id]: value };
        if (q.id === "A1" && value !== "Financial services / lending") delete next.A1b;
        return next;
      });
    } else {
      // Core + industry questions support multi-select
      setAnswers((a) => {
        const current = Array.isArray(a[q.id]) ? a[q.id] : (a[q.id] ? [a[q.id]] : []);
        const isNotSure = value === NOT_SURE_LABEL;
        let next;
        if (current.includes(value)) {
          next = current.filter((x) => x !== value);
        } else if (isNotSure) {
          // Selecting "Not sure" clears other selections
          next = [NOT_SURE_LABEL];
        } else {
          // Selecting a real option removes "Not sure"
          next = [...current.filter((x) => x !== NOT_SURE_LABEL), value];
        }
        return { ...a, [q.id]: next };
      });
    }
    setShowInlineError(false);
  };

  const getAnswer = (q) => {
    if (q.id.startsWith("A")) return profile[q.id];
    const v = answers[q.id];
    return Array.isArray(v) ? v : v ? [v] : [];
  };

  const screenComplete = () => current.questions.every((q) => {
    if (q.conditional && !q.conditional(profile)) return true;
    if (q.id.startsWith("A")) return !!profile[q.id];
    const v = answers[q.id];
    return Array.isArray(v) ? v.length > 0 : !!v;
  });

  const beginAssessment = () => {
    const errs = validateLead(lead);
    if (Object.keys(errs).length > 0) { setTouched(true); return; }
    setPhase("quiz");
    setScreenIdx(0);
  };

  const next = () => {
    if (!screenComplete()) { setShowInlineError(true); return; }
    if (screenIdx < totalScreens - 1) {
      setScreenIdx((i) => i + 1);
    } else {
      setPhase("report");
    }
  };
  const back = () => {
    setShowInlineError(false);
    if (screenIdx > 0) setScreenIdx((i) => i - 1);
    else setPhase("intro");
  };

  const restart = () => {
    setPhase("intro"); setScreenIdx(0); setAnswers({}); setProfile({}); setLead({ name: "", company: "", email: "", mobile: "" }); setTouched(false); setShowInlineError(false);
  };

  const report = useMemo(() => {
    if (phase !== "report") return null;
    return computeReport(profile, answers);
  }, [phase, profile, answers]);

  // Record the completed assessment to the Techmate backend (lead capture).
  useEffect(() => {
    if (phase !== "report" || !report) return;
    recordNavigatorResult({
      name: lead.name,
      company: lead.company,
      email: lead.email,
      mobile: lead.mobile,
      profile,
      answers,
      overall: report.overall,
      tier_label: report.tier.label,
      tier_name: report.tier.tier,
      areas: report.areaScores,
      scope: report.scope.map((s) => ({ name: s.name, readiness: s.readiness, band: s.band })),
      risks: report.risks.slice(0, 10).map((r) => ({ level: r.level, control: r.control, area: r.area, severity: r.severity })),
    }).catch(() => { /* recording is best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <div className="tcc min-h-screen bg-[color:var(--tcc-surface)]">
      {/* top banner */}
      <div className="pt-28 sm:pt-32 print-hide" />

      {phase === "intro" && (
        <IntroScreen lead={lead} setLead={setLead} touched={touched} onBegin={beginAssessment} />
      )}

      {phase === "quiz" && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          {/* progress */}
          <div className="print-hide sticky top-24 sm:top-28 z-20 py-3 bg-[color:var(--tcc-surface)]/90 backdrop-blur">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[color:var(--tcc-ink-soft)]">
                Step {screenIdx + 1} of {totalScreens} · {current.title}
              </div>
              <div className="font-mono-plex text-[11px] font-semibold text-[color:var(--tcc-blue)]">{progress}%</div>
            </div>
            <div className="h-2 rounded-full bg-[color:var(--tcc-surface-2)] overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: "linear-gradient(135deg,#1E77C4,#3FB8E1)" }} />
            </div>
          </div>

          <div key={screenIdx} className="tcc-fade mt-6 space-y-6">
            {current.questions.map((q, qi) => {
              if (q.conditional && !q.conditional(profile)) return null;
              const chosenList = q.id.startsWith("A")
                ? (profile[q.id] ? [profile[q.id]] : [])
                : (Array.isArray(answers[q.id]) ? answers[q.id] : (answers[q.id] ? [answers[q.id]] : []));
              const isProfile = q.id.startsWith("A");
              const options = q.verify ? [...q.options, NOT_SURE_LABEL] : q.options;
              const unanswered = showInlineError && chosenList.length === 0;
              return (
                <div key={q.id} className="rounded-2xl bg-white border border-[color:var(--tcc-line)] p-5 sm:p-6 shadow-sm" data-testid={`tcc-question-${q.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-mono-plex text-[10px] uppercase tracking-wider text-[color:var(--tcc-ink-soft)]">{q.id}</div>
                    {!isProfile && (
                      <div className="font-mono-plex text-[10px] uppercase tracking-wider text-[color:var(--tcc-blue)]">
                        Select all that apply
                      </div>
                    )}
                  </div>
                  <label className="mt-1 block font-sora text-lg sm:text-xl font-bold text-[color:var(--tcc-ink)] leading-snug">
                    {q.question}
                  </label>
                  <div className="mt-4 space-y-2.5" role={isProfile ? "radiogroup" : "group"} aria-label={q.question}>
                    {options.map((opt, oi) => {
                      const selected = chosenList.includes(opt);
                      const shape = isProfile ? "rounded-full" : "rounded-md";
                      return (
                        <button
                          key={oi}
                          type="button"
                          role={isProfile ? "radio" : "checkbox"}
                          aria-checked={selected}
                          onClick={() => setAnswer(q, opt)}
                          data-testid={`tcc-option-${q.id}-${oi}`}
                          className="w-full text-left rounded-xl border px-4 py-3.5 text-[15px] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tcc-blue)]"
                          style={selected ? {
                            borderColor: "var(--tcc-orange)", background: "#FDEEE1", fontWeight: 500, color: "var(--tcc-ink)",
                          } : {
                            borderColor: "var(--tcc-line)", background: "#fff", color: "var(--tcc-ink-soft)",
                          }}
                        >
                          <span className="inline-flex items-center gap-3">
                            <span className={`grid place-items-center h-5 w-5 border shrink-0 ${shape}`} style={{ borderColor: selected ? "var(--tcc-orange)" : "var(--tcc-line)", background: selected ? "var(--tcc-orange)" : "transparent" }}>
                              {selected && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                            </span>
                            {opt}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {unanswered && (
                    <div className="mt-3 font-mono-plex text-[12px]" style={{ color: "var(--tcc-danger)" }}>Please choose at least one option to continue.</div>
                  )}
                </div>
              );
            })}

            <div className="flex items-center justify-between pt-2">
              <button onClick={back} data-testid="tcc-back-btn" className="inline-flex items-center gap-1.5 rounded-full px-5 py-3 font-semibold text-[color:var(--tcc-ink)] border border-[color:var(--tcc-line)] bg-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button onClick={next} data-testid="tcc-next-btn" className="inline-flex items-center gap-1.5 rounded-full px-6 py-3 font-semibold text-white" style={{ background: "linear-gradient(135deg,#1E77C4,#3FB8E1)" }}>
                {screenIdx === totalScreens - 1 ? "See my report" : "Continue"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === "report" && report && (
        <div className="px-4 sm:px-6 lg:px-8 pb-24 pt-4">
          <Report report={report} name={lead.name} company={lead.company} email={lead.email} mobile={lead.mobile} onRestart={restart} />
        </div>
      )}
    </div>
  );
}

function coreTitle(n) {
  return {
    1: "Governance & visibility",
    2: "Protection",
    3: "Detection & response",
    4: "Resilience & data",
  }[n];
}

function validateLead(lead) {
  const errs = {};
  if (!lead.name.trim()) errs.name = "Please enter your name.";
  if (!lead.company.trim()) errs.company = "Please enter your company.";
  if (!lead.email.trim()) errs.email = "Please enter your work email.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) errs.email = "Please enter a valid email address.";
  if (!lead.mobile.trim()) errs.mobile = "Please enter your mobile number.";
  else if (!/^[0-9+\-\s()]{7,20}$/.test(lead.mobile.trim())) errs.mobile = "Please enter a valid mobile number.";
  return errs;
}

function IntroScreen({ lead, setLead, touched, onBegin }) {
  const errs = validateLead(lead);
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <div className="tcc-fade rounded-3xl bg-white border border-[color:var(--tcc-line)] shadow-lg overflow-hidden">
        <div className="p-7 sm:p-10 text-white" style={{ background: "linear-gradient(135deg,#153A68,#0C2136)" }}>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-mono-plex text-[11px] uppercase tracking-wider">
            <ShieldCheck className="h-3.5 w-3.5" /> Techmate Cyber Security &amp; Compliance Check
          </div>
          <h1 className="mt-5 font-sora text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            How resilient is your business, really?
          </h1>
          <p className="mt-3 text-white/80 font-inter text-[15px] leading-relaxed">
            Free · about 4 minutes · plain English · no signup. Your contact details are shared
            securely with Techmate so a consultant can follow up; responses are used only to
            prepare your report.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-white/75 font-mono-plex text-[11px]">
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> ~4 minutes</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Shared securely with Techmate</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Consulting-grade report</span>
          </div>
        </div>
        <div className="p-7 sm:p-10 space-y-4">
          <Field label="Your name" required value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} error={touched ? errs.name : ""} testid="tcc-intro-name" />
          <Field label="Company" required value={lead.company} onChange={(v) => setLead({ ...lead, company: v })} error={touched ? errs.company : ""} testid="tcc-intro-company" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Work email" required value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} error={touched ? errs.email : ""} testid="tcc-intro-email" type="email" />
            <Field label="Mobile number" required value={lead.mobile} onChange={(v) => setLead({ ...lead, mobile: v })} error={touched ? errs.mobile : ""} testid="tcc-intro-mobile" type="tel" />
          </div>
          <button onClick={onBegin} data-testid="tcc-begin-btn" className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-semibold text-white text-lg" style={{ background: "linear-gradient(135deg,#EE6C1F,#FF9124)" }}>
            Begin assessment <ArrowRight className="h-5 w-5" />
          </button>
          <p className="text-center font-mono-plex text-[11px] text-[color:var(--tcc-ink-soft)]">
            {FIRM.name} · {FIRM.address}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, hint, value, onChange, error, testid, type = "text" }) {
  return (
    <div>
      <label className="block font-sora text-sm font-semibold text-[color:var(--tcc-ink)]">
        {label} {required && <span style={{ color: "var(--tcc-orange)" }}>*</span>}
        {hint && <span className="ml-2 font-inter font-normal text-[color:var(--tcc-ink-soft)] text-xs">{hint}</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border px-4 py-3 text-[15px] font-inter focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tcc-blue)]"
        style={{ borderColor: error ? "var(--tcc-danger)" : "var(--tcc-line)", background: "#fff" }}
      />
      {error && <div className="mt-1 font-mono-plex text-[12px]" style={{ color: "var(--tcc-danger)" }}>{error}</div>}
    </div>
  );
}
